/**
 * Мастер создания техкарты — 8 шагов founder одним диалогом (ADR-071 D1).
 *
 * Порядок полей — дословно flow founder: название → культура(ы) →
 * предшественник(и) → плановая урожайность → выбор варианта посевного плана →
 * зелёная плашка «подходит N га · M полей (посмотреть список)» → «Создать и
 * наполнить».
 *
 * Почему культуры — ЧИПЫ (множественный выбор): «культура(ы)» в диктовке — про
 * мастер, а не про модель. Выбрал три культуры — мастер создаст ТРИ карты за
 * один проход (ADR-071 D2, культура на карте одна).
 *
 * Почему плашка живёт на каждой культуре отдельно: авто-площадь считается по
 * правилу «культура × предшественник» из строк варианта плана, и у каждой
 * культуры она своя. Пусто — честный ноль с причиной, а не цифра из воздуха
 * («пусто ≠ мусор»).
 *
 * Уроки e2e founder на staging v1.1742 (три дефекта одного прохода):
 *   1-2. Единственный вариант плана в списке читался как УЖЕ выбранный
 *        («селект подставился») — человек не кликал, scenario не уходил:
 *        ни плашки, ни привязки. Теперь единственный вариант (или «план
 *        сезона» среди нескольких) выбирается сам, выбор помечен явно,
 *        а снятый выбор проговаривается словами, а не молчит.
 *   3.   Предшественник — это ФАКТ прошлых сезонов, он не обязан быть в
 *        номенклатуре хозяйства на новый сезон («Пшеница озимая» жила в
 *        истории Руно, но не в номенклатуре — и правило было не собрать).
 *        Список предшественников берётся из ПОЛНОГО справочника культур;
 *        номенклатура остаётся источником только для культур карты.
 */
import { useEffect, useMemo, useRef, useState } from 'react';
import { Check } from 'lucide-react';
import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { ScrollArea } from '@/components/ui/scroll-area';
import { Skeleton } from '@/components/ui/skeleton';
import { useCropPicker } from '@/components/fields/card/api';
import { useCropsReference } from '@/lib/map/useCropsReference';
import { useFarms } from '@/lib/reference/catalogScopes';
import {
  formatNumber,
  useCreateViaWizard,
  usePlanVariants,
  useWizardPreview,
} from '@/lib/planning/techCards';
import { toast } from '@/lib/toast';
import { cn } from '@/lib/utils';
import { matchesQuery, pickableCrops } from '../plan-variant/AddCropDialog';

export function CreateCardWizard({
  seasonId,
  seasonYear,
  onClose,
  onCreated,
}: {
  seasonId: string;
  seasonYear: number;
  onClose: () => void;
  onCreated: (cardId: string) => void;
}) {
  const crops = useCropPicker();
  // Предшественники — из ПОЛНОГО справочника культур: факт прошлого сезона не
  // обязан входить в номенклатуру нового (дефект 3 e2e founder, staging v1.1742).
  const referenceCrops = useCropsReference();
  const farms = useFarms();
  const create = useCreateViaWizard();

  const [name, setName] = useState('Базовый');
  const [cropIds, setCropIds] = useState<string[]>([]);
  const [predecessorIds, setPredecessorIds] = useState<string[]>([]);
  const [cropQuery, setCropQuery] = useState('');
  const [predQuery, setPredQuery] = useState('');
  const [yieldCHa, setYieldCHa] = useState('');
  const [yieldError, setYieldError] = useState<string | null>(null);
  const [farmId, setFarmId] = useState<string | null>(null);
  const [scenarioId, setScenarioId] = useState<string | null>(null);

  const plans = usePlanVariants(farmId, seasonId);
  const options = useMemo(() => pickableCrops(crops.data ?? []), [crops.data]);
  const predecessorOptions = useMemo(
    () =>
      [...(referenceCrops.data ?? [])]
        .map((c) => ({ id: c.id, name: c.name }))
        .sort((a, b) => a.name.localeCompare(b.name, 'ru')),
    [referenceCrops.data],
  );
  const cropName = (id: string) => options.find((c) => c.id === id)?.name ?? 'культура';

  const toggle = (list: string[], id: string) =>
    list.includes(id) ? list.filter((x) => x !== id) : [...list, id];

  // Единственное хозяйство (частный случай — scope-линза сессии уже сузила
  // список до одной фермы, как у founder с Руно) выбирается само: клик в
  // безальтернативный чип — лишний шаг, на котором и споткнулся e2e.
  // Ровно один раз: снявший выбор руками с автоматикой не борется.
  const autoPickedSingleFarm = useRef(false);
  useEffect(() => {
    if (autoPickedSingleFarm.current || farmId || farms.data === undefined) return;
    if (farms.data.length === 1) {
      autoPickedSingleFarm.current = true;
      setFarmId(farms.data[0].id);
    }
  }, [farmId, farms.data]);

  // Дефекты 1-2 e2e founder: единственный вариант плана выглядел «уже
  // выбранным», клика не было — scenario не уходил ни в предпросмотр, ни в
  // создание. Выбираем сами: единственный вариант, а среди нескольких — «план
  // сезона» (is_selected). Ровно ОДИН раз на хозяйство: человек, снявший выбор
  // руками, не должен бороться с автоматикой.
  const autoPickedFarm = useRef<string | null>(null);
  useEffect(() => {
    if (!farmId || plans.isLoading || plans.data === undefined) return;
    if (autoPickedFarm.current === farmId) return;
    autoPickedFarm.current = farmId;
    const list = plans.data;
    const preferred = list.length === 1 ? list[0] : list.find((p) => p.is_selected);
    if (preferred) setScenarioId(preferred.id);
  }, [farmId, plans.isLoading, plans.data]);

  const submit = () => {
    if (cropIds.length === 0 || !name.trim() || create.isPending) return;
    // Набранное НЕ выбрасываем молча (паттерн #832): «5о» или отрицательное
    // число — ошибка словами с сохранением ввода, а не тихий null. Ноль тоже
    // отбиваем: целевая урожайность 0 ц/га для техкарты бессмысленна — это
    // «выращиваем ничего», и молча создать карту без урожайности хуже отказа.
    const trimmed = yieldCHa.trim();
    const parsedYield = trimmed === '' ? null : Number(trimmed.replace(',', '.'));
    if (parsedYield !== null && (!Number.isFinite(parsedYield) || parsedYield < 0)) {
      setYieldError('Урожайность — число больше нуля, например 55 или 48,5');
      return;
    }
    if (parsedYield !== null && parsedYield === 0) {
      setYieldError('Ноль здесь не бывает — укажите урожайность больше нуля либо оставьте поле пустым');
      return;
    }
    create.mutate(
      {
        seasonId,
        name: name.trim(),
        cropIds,
        predecessorCropIds: predecessorIds,
        targetYieldCHa: parsedYield,
        scenarioId,
        applyStagePreset: true,
      },
      {
        onSuccess: (cards) => {
          toast.success(
            cards.length === 1
              ? 'Техкарта заведена — наполните её работами'
              : `Заведено карт: ${cards.length} — открываем первую`,
          );
          onCreated(cards[0].card_id);
        },
      },
    );
  };

  const farmOptions = [...(farms.data ?? [])].sort((a, b) => a.name.localeCompare(b.name, 'ru'));

  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-h-[85vh] overflow-y-auto sm:max-w-2xl">
        <DialogHeader>
          <DialogTitle>Новая техкарта</DialogTitle>
          <DialogDescription>
            Сезон {seasonYear}. Техкарта нормирует культуру, а не поле. Выберите вариант
            посевного плана — система сама скажет, сколько гектаров подходит под эту
            технологию.
          </DialogDescription>
        </DialogHeader>

        <form
          onSubmit={(e) => {
            e.preventDefault();
            submit();
          }}
        >
          <div className="flex flex-col gap-4">
            <div className="flex flex-col gap-2">
              <Label htmlFor="wizard-name">Название варианта</Label>
              <Input
                id="wizard-name"
                value={name}
                onChange={(e) => setName(e.target.value)}
                maxLength={120}
              />
              <p className="text-xs text-muted-foreground">
                У культуры вариантов несколько — «Базовый», «Интенсив», «Эконом». Различают
                их названием.
              </p>
            </div>

            <MultiCropPicker
              id="wizard-crops"
              label="Культура (можно несколько — получится карта на каждую)"
              query={cropQuery}
              onQuery={setCropQuery}
              loading={crops.isLoading}
              options={options}
              selected={cropIds}
              onToggle={(id) => setCropIds((cur) => toggle(cur, id))}
              emptyHint="В номенклатуре хозяйства нет культур, связанных с общим справочником. Заведите культуру — тогда на неё можно будет составить техкарту."
            />

            <MultiCropPicker
              id="wizard-preds"
              label="Предшественник (можно несколько; пусто — общая карта культуры)"
              query={predQuery}
              onQuery={setPredQuery}
              loading={referenceCrops.isLoading}
              options={predecessorOptions}
              selected={predecessorIds}
              onToggle={(id) => setPredecessorIds((cur) => toggle(cur, id))}
              emptyHint="Справочник культур не загрузился — предшественника выбрать не из чего, карта останется общей по культуре."
              hint="Список — полный справочник культур: предшественник — это факт прошлого сезона, он может не входить в номенклатуру нового. Без предшественника карта общая: покроет строки плана, на которые не нашлось точной."
            />

            <div className="flex flex-col gap-2">
              <Label htmlFor="wizard-yield">Плановая урожайность, ц/га</Label>
              <Input
                id="wizard-yield"
                inputMode="decimal"
                value={yieldCHa}
                aria-invalid={yieldError ? true : undefined}
                className={yieldError ? 'border-destructive focus-visible:ring-destructive' : undefined}
                onChange={(e) => {
                  setYieldCHa(e.target.value);
                  if (yieldError) setYieldError(null);
                }}
                placeholder="например, 55"
              />
              {yieldError && <p className="text-xs text-destructive">{yieldError}</p>}
            </div>

            <div className="flex flex-col gap-2">
              <Label>Вариант посевного плана</Label>
              <div className="flex flex-wrap items-center gap-2">
                <span className="text-xs text-muted-foreground">Хозяйство:</span>
                {farmOptions.map((farm) => (
                  <button
                    key={farm.id}
                    type="button"
                    aria-pressed={farm.id === farmId}
                    onClick={() => {
                      setFarmId((cur) => (cur === farm.id ? null : farm.id));
                      setScenarioId(null);
                    }}
                    className={
                      farm.id === farmId
                        ? 'rounded-full border border-primary bg-primary/10 px-3 py-1 text-sm font-medium text-primary transition-colors'
                        : 'rounded-full border border-border px-3 py-1 text-sm text-muted-foreground transition-colors hover:bg-accent hover:text-foreground'
                    }
                  >
                    {farm.name}
                  </button>
                ))}
              </div>
              {!farmId && (
                <p className="text-xs text-muted-foreground">
                  Без варианта плана карта останется эталоном компании — площадь появится
                  при назначении.
                </p>
              )}
              {farmId && plans.isLoading && <Skeleton className="h-16 w-full" />}
              {farmId && !plans.isLoading && (plans.data ?? []).length === 0 && (
                <p className="text-xs text-muted-foreground">
                  У хозяйства нет вариантов плана на сезон — заведите вариант в
                  планировании, либо создайте карту без него.
                </p>
              )}
              {farmId && (plans.data ?? []).length > 0 && (
                <div className="flex flex-col gap-1">
                  {(plans.data ?? []).map((plan) => {
                    const picked = plan.id === scenarioId;
                    return (
                      <button
                        key={plan.id}
                        type="button"
                        aria-pressed={picked}
                        onClick={() => setScenarioId((cur) => (cur === plan.id ? null : plan.id))}
                        className={
                          picked
                            ? 'flex items-center gap-2 rounded-md border border-primary bg-primary/10 px-3 py-2 text-left text-sm font-medium text-primary'
                            : 'flex items-center gap-2 rounded-md border border-border px-3 py-2 text-left text-sm text-foreground hover:bg-accent'
                        }
                      >
                        {/* Явная галка выбора: единственный вариант в списке без
                            неё читался как «уже выбранный селект» (e2e founder). */}
                        <Check className={cn('h-4 w-4 shrink-0', picked ? 'opacity-100' : 'opacity-0')} />
                        <span>
                          {plan.name}
                          {plan.is_selected ? ' · план сезона' : ''}
                          {picked ? ' — выбран' : ''}
                        </span>
                      </button>
                    );
                  })}
                </div>
              )}
              {farmId && !plans.isLoading && (plans.data ?? []).length > 0 && !scenarioId && (
                <p className="text-xs text-destructive">
                  Вариант не выбран — карта создастся без привязки к плану, гектары не
                  посчитаются. Нажмите на вариант, чтобы выбрать его.
                </p>
              )}
            </div>

            {scenarioId &&
              cropIds.map((cropId) => (
                <CropPreviewPlate
                  key={cropId}
                  scenarioId={scenarioId}
                  cropId={cropId}
                  cropName={cropName(cropId)}
                  predecessorIds={predecessorIds}
                />
              ))}
          </div>

          <DialogFooter className="mt-4">
            <Button type="button" variant="outline" onClick={onClose}>
              Отмена
            </Button>
            <Button type="submit" disabled={cropIds.length === 0 || !name.trim() || create.isPending}>
              {create.isPending ? 'Заводим…' : 'Создать и наполнить'}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}

/**
 * Зелёная плашка «подходит N га · M полей (посмотреть список)» — шаг 7 мастера.
 * Считается ДО создания, тем же правилом, что применит бэк после.
 */
function CropPreviewPlate({
  scenarioId,
  cropId,
  cropName,
  predecessorIds,
}: {
  scenarioId: string;
  cropId: string;
  cropName: string;
  predecessorIds: string[];
}) {
  const preview = useWizardPreview(scenarioId, cropId, predecessorIds);
  const [showFields, setShowFields] = useState(false);

  if (preview.isLoading) return <Skeleton className="h-12 w-full" />;
  if (preview.isError) {
    return (
      <p className="rounded-md border border-border bg-muted p-3 text-sm text-muted-foreground">
        {cropName}: площадь по плану посчитать не удалось — карта всё равно создастся.
      </p>
    );
  }
  const data = preview.data;
  if (!data) return null;

  if (data.fields_count === 0) {
    return (
      <div className="rounded-md border border-border bg-muted p-3 text-sm text-muted-foreground">
        <span className="font-medium text-foreground">{cropName}:</span> подходящих полей в
        варианте нет.
        {data.gaps.length > 0 && <span> {data.gaps[0]}.</span>}
      </div>
    );
  }

  return (
    <div className="rounded-md border border-primary/40 bg-primary/10 p-3 text-sm text-foreground">
      <div className="flex flex-wrap items-center gap-2">
        <span>
          <span className="font-medium">{cropName}:</span> подходит{' '}
          <span className="font-semibold">{formatNumber(data.auto_area_ha)} га</span> ·{' '}
          {data.fields_count === 1 ? '1 поле' : `${data.fields_count} полей`}
        </span>
        <button
          type="button"
          className="text-primary underline-offset-2 hover:underline"
          onClick={() => setShowFields((v) => !v)}
        >
          {showFields ? 'скрыть список' : 'посмотреть список'}
        </button>
      </div>
      {data.gaps.length > 0 && (
        <p className="mt-1 text-xs text-muted-foreground">{data.gaps.join(' · ')}</p>
      )}
      {showFields && (
        <ul className="mt-2 space-y-1 text-xs text-muted-foreground">
          {data.fields.map((field) => (
            <li key={field.id}>
              {field.name} — {formatNumber(field.area_ha)} га
              {field.predecessor_name ? ` · по ${field.predecessor_name.toLowerCase()}` : ''}
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}

/** Мультивыбор культур: чипы выбранного + список «алфавит + поиск» (сквозное правило). */
function MultiCropPicker({
  id,
  label,
  query,
  onQuery,
  loading,
  options,
  selected,
  onToggle,
  emptyHint,
  hint,
}: {
  id: string;
  label: string;
  query: string;
  onQuery: (q: string) => void;
  loading: boolean;
  options: { id: string; name: string }[];
  selected: string[];
  onToggle: (id: string) => void;
  emptyHint: string;
  hint?: string;
}) {
  const shown = options.filter((c) => matchesQuery(c.name, query));
  const nameOf = (cropId: string) => options.find((c) => c.id === cropId)?.name ?? 'культура';

  return (
    <div className="flex flex-col gap-2">
      <Label htmlFor={id}>{label}</Label>
      {selected.length > 0 && (
        <div className="flex flex-wrap gap-2">
          {selected.map((cropId) => (
            <button
              key={cropId}
              type="button"
              onClick={() => onToggle(cropId)}
              className="rounded-full border border-primary bg-primary/10 px-3 py-1 text-sm font-medium text-primary transition-colors"
              aria-label={`Убрать ${nameOf(cropId)}`}
            >
              {nameOf(cropId)} ×
            </button>
          ))}
        </div>
      )}
      <Input id={id} value={query} onChange={(e) => onQuery(e.target.value)} placeholder="Поиск по названию" />
      {loading && <Skeleton className="h-24 w-full" />}
      {!loading && options.length === 0 && <p className="text-sm text-muted-foreground">{emptyHint}</p>}
      {options.length > 0 && (
        <ScrollArea className="max-h-40 rounded-md border border-border">
          <div className="flex flex-col p-1">
            {shown.map((crop) => (
              <button
                key={crop.id}
                type="button"
                aria-pressed={selected.includes(crop.id)}
                onClick={() => onToggle(crop.id)}
                className={
                  selected.includes(crop.id)
                    ? 'rounded-md bg-primary/10 px-3 py-2 text-left text-sm font-medium text-primary'
                    : 'rounded-md px-3 py-2 text-left text-sm text-foreground hover:bg-accent'
                }
              >
                {crop.name}
              </button>
            ))}
            {shown.length === 0 && (
              <p className="px-3 py-2 text-sm text-muted-foreground">По запросу ничего не нашлось.</p>
            )}
          </div>
        </ScrollArea>
      )}
      {hint && <p className="text-xs text-muted-foreground">{hint}</p>}
    </div>
  );
}
